import React, { useEffect, useState } from "react";
import { MapPin, Loader2, Check } from "lucide-react";

import LiveSelfieCapture from "@/components/shared/LiveSelfieCapture";
import { useI18n } from "@/context/I18nContext";

/**
 * CheckInModal — sits on top of LiveSelfieCapture and ALSO asks for the
 * browser's GPS coordinates before the cleaner can confirm. Both permissions
 * are required by spec; if either is denied, the modal blocks the confirm.
 *
 * Props:
 *   - open / onOpenChange
 *   - onSubmit({ blob, dataUrl, lat, lng, accuracy })
 *   - submitting
 */
export default function CheckInModal({ open, onOpenChange, onSubmit, submitting }) {
  const { locale } = useI18n();
  const L = (en, es) => (locale === "es" ? es : en);
  const [coords, setCoords] = useState(null); // { lat, lng, accuracy }
  const [gpsError, setGpsError] = useState("");
  const [requestingGps, setRequestingGps] = useState(false);

  useEffect(() => {
    if (!open) {
      setCoords(null);
      setGpsError("");
      return;
    }
    if (!navigator.geolocation) {
      setGpsError(L("Geolocation is not available on this device.",
                    "La geolocalización no está disponible en este dispositivo."));
      return;
    }
    setRequestingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setRequestingGps(false);
      },
      (err) => {
        const msg = err.code === err.PERMISSION_DENIED
          ? L("Location permission was denied. Both camera and location are required to check in.",
               "Se denegó el permiso de ubicación. Se requiere cámara Y ubicación para hacer el check-in.")
          : L("We couldn't read your location. Make sure you are outdoors or close to a window.",
               "No pudimos leer tu ubicación. Asegúrate de estar en exterior o cerca de una ventana.");
        setGpsError(msg);
        setRequestingGps(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }, [open, locale]);

  const handleCapture = async (blob, dataUrl) => {
    if (!coords) return;
    await onSubmit({ blob, dataUrl, ...coords });
  };

  const gpsBlock = (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-3 text-sm" data-testid="checkin-gps-status">
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-[#0EA5A4]" />
        {requestingGps ? (
          <span className="text-[#4B5563] flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {L("Reading your location…", "Leyendo tu ubicación…")}
          </span>
        ) : coords ? (
          <span className="text-[#0F766E] font-medium flex items-center gap-1.5">
            <Check className="w-4 h-4" />
            {L("Location ready", "Ubicación lista")}
            <span className="text-xs text-[#9CA3AF] font-normal ml-1">
              ({coords.lat.toFixed(5)}, {coords.lng.toFixed(5)} · ±{Math.round(coords.accuracy)}m)
            </span>
          </span>
        ) : (
          <span className="text-red-600">{gpsError}</span>
        )}
      </div>
    </div>
  );

  return (
    <LiveSelfieCapture
      open={open}
      onOpenChange={onOpenChange}
      title={L("Check in at the customer's home", "Check-in en el hogar del cliente")}
      description={L("Take a quick selfie and share your location so the customer knows you've arrived.",
                     "Toma una selfie rápida y comparte tu ubicación para que el cliente sepa que llegaste.")}
      onCapture={handleCapture}
      submitting={submitting}
      submitLabel={L("I'm here, send selfie", "Llegué, enviar selfie")}
      extra={gpsBlock}
    />
  );
}
